import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Search, RotateCcw, Upload, FileText, Inbox } from "lucide-react";
import { fileUrl, uploadFile, errMsg } from "../lib/api";
import { toast } from "sonner";

export const PageTitle = ({ title, subtitle, actions, testId = "page-title" }) => (
  <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
    <div>
      <h1 data-testid={testId} className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2 no-print">{actions}</div>}
  </div>
);

export const Card = ({ children, className = "", ...p }) => <div className={`card ${className}`} {...p}>{children}</div>;

export const StatCard = ({ icon: Icon, value, label, tone = "emerald", testId, wrap = false }) => {
  const tones = { emerald: "bg-emerald-50 text-emerald-600", orange: "bg-orange-50 text-orange-600", sky: "bg-sky-50 text-sky-600", rose: "bg-rose-50 text-rose-600", violet: "bg-violet-50 text-violet-600", amber: "bg-amber-50 text-amber-600" };
  return (
    <Card className="p-5 flex items-center gap-4 hover:shadow-md transition-shadow" data-testid={testId}>
      <div className={`p-3 rounded-xl shrink-0 ${tones[tone]}`}><Icon size={22} /></div>
      <div className="min-w-0">
        <div className={`font-extrabold text-slate-900 leading-tight ${wrap ? "text-lg break-words" : "text-2xl truncate"}`}>{value}</div>
        <div className="text-xs text-slate-500 font-medium mt-0.5">{label}</div>
      </div>
    </Card>
  );
};

export const Field = ({ label, children, className = "", required }) => (
  <div className={className}>
    {label && <label className="label">{label}{required && <span className="text-rose-500"> *</span>}</label>}
    {children}
  </div>
);

export const Input = (p) => <input className="input" {...p} />;
export const Textarea = (p) => <textarea className="input min-h-[90px]" {...p} />;
export const Select = ({ options = [], placeholder, children, ...p }) => (
  <select className="input" {...p}>
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
    {children}
  </select>
);

export const FilterBar = ({ children, onApply, onReset, testId = "filter-bar" }) => (
  <Card className="p-4 mb-5 no-print" data-testid={testId}>
    <div className="flex flex-wrap items-end gap-3">
      {children}
      <div className="flex gap-2 ml-auto">
        <button data-testid="filter-apply-btn" className="btn-primary" onClick={onApply}><Search size={15} /> Terapkan</button>
        <button data-testid="filter-reset-btn" className="btn-outline" onClick={onReset}><RotateCcw size={15} /> Reset</button>
      </div>
    </div>
  </Card>
);

export const Table = ({ cols, rows, empty = "Belum ada data", testId = "data-table", rowKey = "id", footer }) => (
  <Card className="overflow-x-auto" data-testid={testId}>
    <table className="w-full text-sm">
      <thead>
        <tr>{cols.map((c, i) => <th key={i} className={`${c.brand ? "th-brand" : "th"} ${c.right ? "text-right" : ""} ${c.center ? "text-center" : ""}`} style={c.width ? { width: c.width } : undefined}>{c.h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-12 text-center text-slate-400"><Inbox className="mx-auto mb-2" size={28} /><span data-testid="table-empty">{empty}</span></td></tr>}
        {rows.map((r, ri) => (
          <tr key={r[rowKey] || ri} className="hover:bg-emerald-50/40 transition-colors" data-testid={`table-row-${ri}`}>
            {cols.map((c, i) => <td key={i} className={`td ${c.right ? "text-right" : ""} ${c.center ? "text-center" : ""} ${c.mono ? "font-mono text-xs" : ""} ${c.brand ? "font-semibold text-slate-900" : ""} ${c.nowrap ? "whitespace-nowrap" : ""}`}>{c.render ? c.render(r, ri) : r[c.k] ?? "-"}</td>)}
          </tr>
        ))}
      </tbody>
      {footer && <tfoot>{footer}</tfoot>}
    </table>
  </Card>
);

export const Badge = ({ children, tone = "slate" }) => {
  const t = { slate: "bg-slate-100 text-slate-700", green: "bg-emerald-100 text-emerald-800", red: "bg-rose-100 text-rose-700", amber: "bg-amber-100 text-amber-800", sky: "bg-sky-100 text-sky-800", orange: "bg-orange-100 text-orange-800", violet: "bg-violet-100 text-violet-800" };
  return <span className={`badge ${t[tone]}`}>{children}</span>;
};
export const StatusBadge = ({ status }) => <Badge tone={status === "aktif" ? "green" : "red"}>{status === "aktif" ? "Aktif" : "Tidak Aktif"}</Badge>;
export const HadirBadge = ({ s }) => {
  const m = { H: ["Hadir", "green"], S: ["Sakit", "amber"], I: ["Izin", "sky"], BDR: ["BDR", "violet"], B: ["Belum Absen", "slate"] };
  const [l, t] = m[s] || ["-", "slate"];
  return <Badge tone={t}>{l}</Badge>;
};

export const Modal = ({ open, onClose, title, children, wide, testId = "modal" }) => {
  useEffect(() => {
    if (!open) return;
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", h); document.body.style.overflow = prev; };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/50 backdrop-blur-sm p-4 sm:p-8" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div data-testid={testId} className={`card w-full ${wide ? "max-w-4xl" : "max-w-2xl"} page-enter my-auto max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] flex flex-col`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <button data-testid="modal-close-btn" className="btn-ghost btn-sm" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body
  );
};

export const Confirm = ({ open, onClose, onConfirm, text = "Data yang dihapus tidak dapat dikembalikan." }) => (
  <Modal open={open} onClose={onClose} title="Konfirmasi Hapus" testId="confirm-modal">
    <p className="text-sm text-slate-600">{text}</p>
    <div className="flex justify-end gap-2 mt-6">
      <button className="btn-outline" onClick={onClose} data-testid="confirm-cancel-btn">Batal</button>
      <button className="btn-danger" onClick={onConfirm} data-testid="confirm-delete-btn">Hapus</button>
    </div>
  </Modal>
);

export const FormActions = ({ onCancel, saving, label = "Simpan" }) => (
  <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 mt-6">
    <button type="button" className="btn-outline" onClick={onCancel} data-testid="form-cancel-btn">Batal</button>
    <button type="submit" className="btn-primary" disabled={saving} data-testid="form-submit-btn">{saving ? "Menyimpan..." : label}</button>
  </div>
);

export const FileUpload = ({ value, onChange, accept = "image/*,.pdf,.doc,.docx", label = "Upload file", testId = "file-upload", preview }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { const r = await uploadFile(f); onChange(r.path, f.name); toast.success("File terunggah"); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      {preview && value && <img src={fileUrl(value)} alt="" className="h-16 w-16 rounded-lg object-cover border" />}
      {!preview && value && <a href={fileUrl(value)} target="_blank" rel="noreferrer" className="text-emerald-700 text-sm inline-flex items-center gap-1 hover:underline"><FileText size={14} /> Lihat file</a>}
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={handle} data-testid={`${testId}-input`} />
      <button type="button" className="btn-outline btn-sm" onClick={() => ref.current.click()} disabled={busy} data-testid={testId}><Upload size={14} /> {busy ? "Mengunggah..." : value ? "Ganti file" : label}</button>
    </div>
  );
};

export const Avatar = ({ src, name, size = 12 }) => (
  src ? <img src={fileUrl(src)} alt={name} className={`h-${size} w-${size} rounded-full object-cover border-2 border-white shadow`} style={{ height: size * 4, width: size * 4 }} />
    : <div className="rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold border-2 border-white shadow" style={{ height: size * 4, width: size * 4, fontSize: size * 1.4 }}>{(name || "?").charAt(0).toUpperCase()}</div>
);

export const InfoGrid = ({ items }) => (
  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
    {items.map(([k, v]) => <div key={k}><dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{k}</dt><dd className="text-sm text-slate-900 mt-0.5 font-medium">{v || "-"}</dd></div>)}
  </dl>
);

export const Loading = () => <div className="flex items-center justify-center py-16 text-slate-400 text-sm" data-testid="loading"><div className="h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mr-3" />Memuat...</div>;
